import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fetchTrailerKey } from '../tmdb';
import { TVShow } from '../types';

interface TrailerDialogProps {
  show: TVShow | null;
  onClose: () => void;
}

// undefined = still loading, null = TMDB has no trailer.
type TrailerKey = string | null | undefined;

const TrailerDialog = ({ show, onClose }: TrailerDialogProps) => {
  const [trailerKey, setTrailerKey] = useState<TrailerKey>(undefined);

  const tmdbId = show?.tmdbId;

  useEffect(() => {
    if (!tmdbId) return;
    setTrailerKey(undefined);

    let cancelled = false;
    fetchTrailerKey(tmdbId)
      .then((key) => {
        if (!cancelled) setTrailerKey(key);
      })
      .catch(() => {
        if (!cancelled) setTrailerKey(null);
      });

    return () => {
      cancelled = true;
    };
  }, [tmdbId]);

  if (!show) return null;

  const youtubeSearch = `https://www.youtube.com/results?search_query=${encodeURIComponent(
    `${show.title} trailer`
  )}`;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h5" sx={{ lineHeight: 1.1, minWidth: 0 }}>
          {show.title} — Trailer
        </Typography>
        <IconButton aria-label="Close" onClick={onClose} sx={{ ml: 'auto' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          aspectRatio: '16 / 9',
          bgcolor: 'common.black',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {trailerKey === undefined ? (
          <CircularProgress color="primary" />
        ) : trailerKey ? (
          <Box
            component="iframe"
            src={`https://www.youtube-nocookie.com/embed/${trailerKey}?autoplay=1&playsinline=1&rel=0`}
            title={`${show.title} trailer`}
            allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
            allowFullScreen
            sx={{ width: '100%', height: '100%', border: 0, display: 'block' }}
          />
        ) : (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Typography sx={{ color: 'common.white', mb: 2 }}>
              No trailer on TMDB for this show.
            </Typography>
            <Button
              variant="text"
              color="inherit"
              href={youtubeSearch}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: 'common.white' }}
            >
              Search YouTube
            </Button>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default TrailerDialog;
